import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Check, ArrowUpRight, Calendar, AlertCircle, Zap, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../lib/api';

// Stripe Payment Links
const STRIPE_LINKS = {
  lite: 'https://buy.stripe.com/9B69AU82x9JPfPe2w4',
  business: 'https://buy.stripe.com/aFaaEY6Yt5tz0Ukb2A',
  enterprise: 'https://buy.stripe.com/aFadRabeJ1dj7iIgmU'
};

const PLANS = {
  free: { name: 'Free', price: 0, conversations: 100, color: 'gray' },
  lite: { name: 'Lite', price: 29, conversations: 500, color: 'blue' },
  business: { name: 'Business', price: 39, conversations: 1000, color: 'indigo' },
  enterprise: { name: 'Enterprise', price: 400, conversations: 15000, color: 'purple' }
};

const BillingPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/subscription');

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      } else {
        // User has no subscription yet - show free tier
        setSubscription({
          plan: 'free',
          status: 'active',
          conversations_limit: 100,
          conversations_used: 0
        });
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(t('billing.loadError'));
      // Default to free plan on error
      setSubscription({
        plan: 'free',
        status: 'active',
        conversations_limit: 100,
        conversations_used: 0
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleUpgrade = (planId) => {
    let url = STRIPE_LINKS[planId];
    if (user?.email) {
      url += `?prefilled_email=${encodeURIComponent(user.email)}`;
    }
    window.open(url, '_blank');
  };

  const handleManageSubscription = () => {
    // Stripe Customer Portal - would need to be implemented
    window.open('https://billing.stripe.com/p/login/test_xxx', '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const currentPlan = PLANS[subscription?.plan] || PLANS.free;
  const conversationsUsed = subscription?.conversations_used || 0;
  const conversationsLimit = subscription?.conversations_limit || 100;
  const usagePercentage = Math.min((conversationsUsed / conversationsLimit) * 100, 100);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('billing.title')}</h1>
        <p className="text-gray-500">{t('billing.subtitle')}</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('billing.currentPlan')}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${currentPlan.color}-100 text-${currentPlan.color}-700`}>
                {currentPlan.name}
              </span>
              {subscription?.status === 'active' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  {t('common.active')}
                </span>
              )}
              {subscription?.status === 'past_due' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  {t('billing.paymentPending')}
                </span>
              )}
              {subscription?.status === 'cancelled' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {t('billing.cancelled')}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-gray-900">${currentPlan.price}</span>
            <span className="text-gray-500">{t('billing.perMonth')}</span>
          </div>
        </div>

        {/* Usage Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">{t('billing.conversationsThisMonth')}</span>
            <span className="text-sm text-gray-500">
              {conversationsUsed.toLocaleString()} / {conversationsLimit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                usagePercentage >= 90 ? 'bg-red-500' : usagePercentage >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
              }`}
              style={{ width: `${usagePercentage}%` }}
            />
          </div>
          {usagePercentage >= 90 && (
            <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {t('billing.nearLimit')}
            </p>
          )}
        </div>

        {/* Period Info */}
        {subscription?.current_period_end && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>
              {t('billing.nextRenewal')}: {new Date(subscription.current_period_end).toLocaleDateString(language)}
            </span>
          </div>
        )}
      </div>

      {/* Upgrade Options */}
      {subscription?.plan !== 'enterprise' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('billing.upgrade')}</h2>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Lite */}
            {subscription?.plan === 'free' && (
              <div className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
                <h3 className="font-semibold text-gray-900">Lite</h3>
                <p className="text-2xl font-bold text-gray-900 mt-1">$29<span className="text-sm font-normal text-gray-500">{t('billing.perMonth')}</span></p>
                <p className="text-sm text-gray-500 mt-1">500 {t('billing.conversationsPerMonth')}</p>
                <ul className="mt-3 space-y-1">
                  <li className="text-sm text-gray-600 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-500" /> {t('billing.websiteChat')}
                  </li>
                  <li className="text-sm text-gray-600 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-500" /> {t('billing.qrCodes')}
                  </li>
                </ul>
                <Button
                  onClick={() => handleUpgrade('lite')}
                  className="w-full mt-4"
                  variant="outline"
                >
                  {t('billing.upgradeTo')} Lite
                </Button>
              </div>
            )}

            {/* Business */}
            {(subscription?.plan === 'free' || subscription?.plan === 'lite') && (
              <div className="border-2 border-blue-500 rounded-xl p-4 relative">
                <span className="absolute -top-3 left-4 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {t('billing.recommended')}
                </span>
                <h3 className="font-semibold text-gray-900">Business</h3>
                <p className="text-2xl font-bold text-gray-900 mt-1">$39<span className="text-sm font-normal text-gray-500">{t('billing.perMonth')}</span></p>
                <p className="text-sm text-gray-500 mt-1">1,000 {t('billing.conversationsPerMonth')}</p>
                <ul className="mt-3 space-y-1">
                  <li className="text-sm text-gray-600 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-500" /> {t('billing.whatsappInstagram')}
                  </li>
                  <li className="text-sm text-gray-600 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-500" /> {t('billing.chatgptIntegrated')}
                  </li>
                  <li className="text-sm text-gray-600 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-500" /> {t('billing.massBroadcasts')}
                  </li>
                </ul>
                <Button
                  onClick={() => handleUpgrade('business')}
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  {t('billing.upgradeTo')} Business
                </Button>
              </div>
            )}

            {/* Enterprise */}
            <div className="border border-gray-200 rounded-xl p-4 bg-slate-50">
              <h3 className="font-semibold text-gray-900">Enterprise</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">$400<span className="text-sm font-normal text-gray-500">{t('billing.perMonth')}</span></p>
              <p className="text-sm text-gray-500 mt-1">15,000 {t('billing.conversationsPerMonth')}</p>
              <ul className="mt-3 space-y-1">
                <li className="text-sm text-gray-600 flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> {t('billing.everythingInBusiness')}
                </li>
                <li className="text-sm text-gray-600 flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> {t('billing.support247')}
                </li>
                <li className="text-sm text-gray-600 flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> {t('billing.dedicatedManager')}
                </li>
              </ul>
              <Button
                onClick={() => handleUpgrade('enterprise')}
                className="w-full mt-4 bg-slate-900 hover:bg-slate-800"
              >
                {t('pricing.talkToSales')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Subscription */}
      {subscription?.plan !== 'free' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('billing.manageSubscription')}</h2>
          <p className="text-gray-500 mb-4">
            {t('billing.manageSubDesc')}
          </p>
          <Button variant="outline" onClick={handleManageSubscription}>
            <CreditCard className="w-4 h-4 mr-2" />
            {t('billing.openBillingPortal')}
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Usage Tips */}
      <div className="mt-6 bg-blue-50 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Zap className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900">{t('billing.usageTip')}</h3>
            <p className="text-blue-700 text-sm mt-1">
              {t('billing.usageTipDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
